import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Upload, User, Briefcase, CalendarClock, FileKey, X, Trash2, Eye, EyeOff, Info } from "lucide-react";

const PINK = "#C4738A";
const MAROON = "#7C2D3E";
const inp = "h-10 w-full px-3.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 placeholder:text-slate-300 focus:outline-none focus:border-[#C4738A] focus:ring-2 focus:ring-[#C4738A]/10 transition-all";
const lbl = "block text-xs font-semibold text-slate-700 mb-1.5";
const hint = "text-[10px] text-slate-400 mt-1";

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd);
  return data.url;
}

const TABS = [
  { label: "Data Pribadi", Icon: User },
  { label: "Pekerjaan", Icon: Briefcase },
  { label: "Jadwal & Gaji", Icon: CalendarClock },
  { label: "Akses & Dokumen", Icon: FileKey },
];

const DEFAULT_ROLES = [
  ["hr", "HR / Admin"], ["supervisor", "Supervisor"], ["kasir", "Kasir"], ["crew", "Crew Store"],
  ["gudang", "Gudang"], ["logistik", "Admin Logistik"], ["host_live", "Host Live"], ["karyawan", "Karyawan"],
];
const DEFAULT_STATUSES = [["tetap", "Karyawan Tetap"], ["kontrak", "Kontrak"], ["part_time", "Part-time"]];

export const PERM_GROUPS = [
  { label: "Pesanan", perms: [["orders.view", "Lihat Pesanan"], ["orders.manage", "Kelola Pesanan"]] },
  { label: "Produk & Stok", perms: [["products.view", "Lihat Produk"], ["products.manage", "Kelola Produk"], ["stock.view", "Lihat Stok"], ["stock.manage", "Kelola Stok"]] },
  { label: "Toko & Pelanggan", perms: [["reviews.view", "Lihat Ulasan"], ["reviews.manage", "Kelola Ulasan"], ["vouchers.manage", "Voucher & Promo"], ["customers.view", "Lihat Pelanggan"], ["customers.manage", "Kelola Pelanggan"]] },
  { label: "HR", perms: [["employees.view", "Lihat Karyawan"], ["employees.manage", "Kelola Karyawan"], ["attendance.view", "Lihat Absensi"], ["attendance.manage", "Kelola Absensi"], ["schedule.manage", "Jadwal & Shift"], ["leave.view", "Lihat Izin"], ["leave.manage", "Kelola Izin"], ["payroll.view", "Lihat Payroll"], ["payroll.manage", "Kelola Payroll"], ["payroll.approve", "Approve Payroll"], ["kasbon.view", "Lihat Kasbon"], ["kasbon.manage", "Kelola Kasbon"], ["kasbon.approve", "Approve Kasbon"], ["tasks.view", "Lihat Tugas"], ["tasks.manage", "Kelola Tugas"], ["announcements.manage", "Pengumuman"], ["violations.manage", "Pelanggaran"]] },
  { label: "POS & Laporan", perms: [["pos.use", "Gunakan POS"], ["pos.approve", "Approve POS"], ["pos.report", "Laporan POS"], ["reports.view", "Laporan Penjualan"], ["dashboard.view", "Dashboard"]] },
  { label: "Sistem", perms: [["settings.manage", "Pengaturan"], ["audit.view", "Audit Log"]] },
];

const DAYS = [["sen", "Senin"], ["sel", "Selasa"], ["rab", "Rabu"], ["kam", "Kamis"], ["jum", "Jumat"], ["sab", "Sabtu"], ["min", "Minggu"]];
const RELATIONS = ["Orang Tua", "Pasangan", "Saudara", "Anak", "Lainnya"];

const blank = {
  name: "", email: "", password: "", role: "karyawan", permissions: [],
  phone: "", photo: "", nik: "", birth_place: "", birth_date: "", gender: "",
  address: "", emergency_name: "", emergency_relation: "", emergency_phone: "",
  joined_at: "", employment_status: "tetap", job_note: "",
  work_days: ["sen", "sel", "rab", "kam", "jum", "sab"], shift_start: "09:00", shift_end: "17:00",
  salary_type: "bulanan", base_salary: "", allowance: "", documents: [], active: true,
};

export default function AdminEmployeeForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [form, setForm] = useState(blank);
  const [empId, setEmpId] = useState("");
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [docName, setDocName] = useState("");
  const photoRef = useRef(null);
  const docRef = useRef(null);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/admin/master/roles").then((r) => setRoles(r.data.filter((x) => x.id !== "owner").map((x) => [x.id, x.label]))).catch(() => {});
    api.get("/admin/master/emp-statuses").then((r) => setStatuses(r.data.map((x) => [x.id, x.label]))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/employees/${id}`).then((r) => {
      const u = r.data;
      setEmpId(u.employee_id || "");
      setForm({
        ...blank,
        ...Object.fromEntries(Object.keys(blank).filter((k) => k !== "password" && u[k] !== undefined && u[k] !== null).map((k) => [k, u[k]])),
        password: "",
        base_salary: u.base_salary ?? "", allowance: u.allowance ?? "",
      });
    }).catch(() => { toast.error("Karyawan tidak ditemukan"); nav("/admin/employees"); });
  }, [id]);

  const stepDone = [
    Boolean(form.name.trim() && form.gender && form.phone.trim() && form.address.trim()),
    Boolean(form.role),
    Boolean(form.work_days.length || form.base_salary !== "" || form.joined_at),
    Boolean(form.email.trim() && (isEdit || form.password.length >= 6)),
  ];
  const doneCount = stepDone.filter(Boolean).length;

  const pickPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try { setF("photo", await uploadImage(f)); toast.success("Foto terunggah"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Gagal mengunggah foto"); }
  };

  const pickDoc = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const url = await uploadImage(f);
      setF("documents", [...form.documents, { name: docName.trim() || f.name, url }]);
      setDocName("");
      toast.success("Dokumen terunggah");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal mengunggah dokumen"); }
  };

  const togglePerm = (p) => setF("permissions", form.permissions.includes(p) ? form.permissions.filter((x) => x !== p) : [...form.permissions, p]);
  const toggleDay = (d) => setF("work_days", form.work_days.includes(d) ? form.work_days.filter((x) => x !== d) : [...form.work_days, d]);

  const validate = (asDraft) => {
    if (!form.name.trim()) { setStep(0); return "Nama lengkap wajib diisi"; }
    if (!form.gender) { setStep(0); return "Jenis kelamin wajib dipilih"; }
    if (!form.phone.trim()) { setStep(0); return "Nomor WhatsApp wajib diisi"; }
    if (!form.address.trim()) { setStep(0); return "Alamat domisili wajib diisi"; }
    if (!form.email.trim()) { setStep(3); return "Email akun wajib diisi"; }
    if (!isEdit && form.password.length < 6) { setStep(3); return "Password akun minimal 6 karakter"; }
    return null;
  };

  const save = async (asDraft) => {
    const err = validate(asDraft);
    if (err) return toast.error(err);
    setSaving(asDraft ? "draft" : "aktif");
    const payload = {
      ...form,
      active: !asDraft,
      base_salary: parseInt(form.base_salary) || 0,
      allowance: parseInt(form.allowance) || 0,
      password: form.password || null,
    };
    try {
      if (isEdit) {
        await api.put(`/admin/employees/${id}`, payload);
        toast.success(asDraft ? "Perubahan disimpan — karyawan nonaktif (draft)" : "Data karyawan diperbarui");
      } else {
        const { data } = await api.post("/admin/employees", payload);
        toast.success(`Karyawan tersimpan${asDraft ? " sebagai draft (nonaktif)" : ""} — ID ${data.employee_id}`);
      }
      nav("/admin/employees");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan karyawan"); }
    finally { setSaving(null); }
  };

  const next = () => {
    if (step === 0 && !(form.name.trim() && form.gender && form.phone.trim() && form.address.trim())) return toast.error("Lengkapi kolom wajib (*) di Data Pribadi dulu");
    setStep((s) => Math.min(3, s + 1));
  };

  return (
    <div data-testid="emp-wizard" className="space-y-4 pb-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button onClick={() => nav("/admin/employees")} className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-[#C4738A] mb-1.5 transition-colors"><ArrowLeft className="w-3.5 h-3.5" /> Karyawan</button>
          <h1 className="font-display text-2xl font-bold text-slate-900">{isEdit ? "Ubah Karyawan" : "Tambah Karyawan"}</h1>
          <p className="text-xs text-slate-400">{isEdit ? "Perbarui informasi karyawan." : "Lengkapi informasi karyawan baru."}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="emp-cancel" onClick={() => nav("/admin/employees")} className="h-10 px-5 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 transition-colors">Batal</button>
          <button data-testid="emp-save" onClick={() => save(false)} disabled={!!saving}
            className="h-10 px-5 rounded-lg text-white text-xs font-semibold transition-colors disabled:opacity-50" style={{ backgroundColor: MAROON }}>
            {saving === "aktif" ? "Menyimpan..." : "Simpan Karyawan"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t, i) => (
          <button key={t.label} data-testid={`emp-tab-${i}`} onClick={() => setStep(i)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${step === i ? "border-[#C4738A] text-[#C4738A]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <t.Icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 items-start">
        {/* Konten utama */}
        <div className="xl:col-span-2 space-y-5">
          {step === 0 && (
            <>
              <section className="bg-white rounded-2xl border border-slate-100 p-6">
                <h2 className="font-display font-bold text-sm text-slate-900 mb-5">Informasi Pribadi</h2>
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="flex flex-col items-center gap-2 flex-shrink-0">
                    <div className="w-28 h-28 rounded-full bg-slate-100 overflow-hidden border border-slate-200">
                      {form.photo ? <img src={form.photo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><User className="w-10 h-10" /></div>}
                    </div>
                    <button data-testid="emp-photo-btn" onClick={() => photoRef.current?.click()} className="h-9 px-4 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 flex items-center gap-1.5 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors">
                      <Upload className="w-3.5 h-3.5" /> Unggah Foto
                    </button>
                    <p className={hint}>JPG atau PNG, maks. 2 MB.</p>
                    <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 flex-1">
                    <div><label className={lbl}>Nama Lengkap <span className="text-[#C4738A]">*</span></label>
                      <input data-testid="emp-name" value={form.name} onChange={(e) => setF("name", e.target.value)} placeholder="Masukkan nama lengkap" className={inp} /></div>
                    <div><label className={lbl}>NIK KTP</label>
                      <input data-testid="emp-nik" value={form.nik} onChange={(e) => setF("nik", e.target.value.replace(/\D/g, "").slice(0, 16))} placeholder="16 digit NIK" className={inp} /></div>
                    <div><label className={lbl}>Tempat Lahir</label>
                      <input data-testid="emp-birth-place" value={form.birth_place} onChange={(e) => setF("birth_place", e.target.value)} placeholder="Masukkan tempat lahir" className={inp} /></div>
                    <div><label className={lbl}>Tanggal Lahir</label>
                      <input data-testid="emp-birth-date" type="date" value={form.birth_date} onChange={(e) => setF("birth_date", e.target.value)} className={inp} /></div>
                    <div><label className={lbl}>Jenis Kelamin <span className="text-[#C4738A]">*</span></label>
                      <select data-testid="emp-gender" value={form.gender} onChange={(e) => setF("gender", e.target.value)} className={inp}>
                        <option value="">Pilih jenis kelamin</option><option value="perempuan">Perempuan</option><option value="laki-laki">Laki-laki</option>
                      </select></div>
                    <div><label className={lbl}>Nomor WhatsApp <span className="text-[#C4738A]">*</span></label>
                      <input data-testid="emp-phone" value={form.phone} onChange={(e) => setF("phone", e.target.value)} placeholder="08xxxxxxxxxx" className={inp} /></div>
                    <div className="md:col-span-2"><label className={lbl}>Alamat Domisili <span className="text-[#C4738A]">*</span></label>
                      <textarea data-testid="emp-address" value={form.address} onChange={(e) => setF("address", e.target.value)} rows={3} placeholder="Masukkan alamat domisili lengkap" className={`${inp} h-auto py-2.5`} /></div>
                  </div>
                </div>
              </section>
              <section className="bg-white rounded-2xl border border-slate-100 p-6">
                <h2 className="font-display font-bold text-sm text-slate-900 mb-5">Kontak Darurat</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4">
                  <div><label className={lbl}>Nama Kontak</label>
                    <input data-testid="emp-emg-name" value={form.emergency_name} onChange={(e) => setF("emergency_name", e.target.value)} placeholder="Masukkan nama kontak" className={inp} /></div>
                  <div><label className={lbl}>Hubungan</label>
                    <select data-testid="emp-emg-relation" value={form.emergency_relation} onChange={(e) => setF("emergency_relation", e.target.value)} className={inp}>
                      <option value="">Pilih hubungan</option>{RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select></div>
                  <div><label className={lbl}>Nomor Telepon</label>
                    <input data-testid="emp-emg-phone" value={form.emergency_phone} onChange={(e) => setF("emergency_phone", e.target.value)} placeholder="08xxxxxxxxxx" className={inp} /></div>
                </div>
              </section>
            </>
          )}

          {step === 1 && (
            <section className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
              <h2 className="font-display font-bold text-sm text-slate-900">Informasi Pekerjaan</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
                <div><label className={lbl}>Jabatan / Role Sistem <span className="text-[#C4738A]">*</span></label>
                  <select data-testid="emp-role" value={form.role} onChange={(e) => setF("role", e.target.value)} className={inp}>
                    {roles.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <p className={hint}>Role menentukan halaman tujuan & permission bawaan karyawan</p></div>
                <div><label className={lbl}>Tanggal Bergabung</label>
                  <input data-testid="emp-joined" type="date" value={form.joined_at} onChange={(e) => setF("joined_at", e.target.value)} className={inp} /></div>
                <div><label className={lbl}>Status Kepegawaian</label>
                  <select data-testid="emp-empstatus" value={form.employment_status} onChange={(e) => setF("employment_status", e.target.value)} className={inp}>
                    {statuses.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select></div>
                <div><label className={lbl}>Status Akun</label>
                  <select data-testid="emp-active" value={form.active ? "1" : "0"} onChange={(e) => setF("active", e.target.value === "1")} className={inp}>
                    <option value="1">Aktif</option><option value="0">Nonaktif (draft)</option>
                  </select></div>
                <div className="md:col-span-2"><label className={lbl}>Catatan Pekerjaan</label>
                  <textarea data-testid="emp-jobnote" value={form.job_note} onChange={(e) => setF("job_note", e.target.value)} rows={3} placeholder="Penempatan toko, tanggung jawab khusus, dsb." className={`${inp} h-auto py-2.5`} /></div>
              </div>
            </section>
          )}

          {step === 2 && (
            <section className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
              <h2 className="font-display font-bold text-sm text-slate-900">Jadwal Kerja Default</h2>
              <div>
                <label className={lbl}>Hari Kerja</label>
                <div className="flex gap-2 flex-wrap">
                  {DAYS.map(([v, l]) => (
                    <button key={v} type="button" data-testid={`emp-day-${v}`} onClick={() => toggleDay(v)}
                      className={`h-9 px-4 rounded-full text-xs font-semibold border transition-colors ${form.work_days.includes(v) ? "border-[#C4738A] text-[#C4738A] bg-rose-50/60" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-5 gap-y-4 max-w-md">
                <div><label className={lbl}>Jam Masuk</label>
                  <input data-testid="emp-shift-start" type="time" value={form.shift_start} onChange={(e) => setF("shift_start", e.target.value)} className={inp} /></div>
                <div><label className={lbl}>Jam Pulang</label>
                  <input data-testid="emp-shift-end" type="time" value={form.shift_end} onChange={(e) => setF("shift_end", e.target.value)} className={inp} /></div>
              </div>
              <h2 className="font-display font-bold text-sm text-slate-900 pt-2">Struktur Gaji</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4">
                <div><label className={lbl}>Tipe Gaji</label>
                  <select data-testid="emp-salary-type" value={form.salary_type} onChange={(e) => setF("salary_type", e.target.value)} className={inp}>
                    <option value="bulanan">Bulanan</option><option value="harian">Harian</option>
                  </select></div>
                <div><label className={lbl}>Gaji Pokok (Rp)</label>
                  <input data-testid="emp-base-salary" type="number" min="0" value={form.base_salary} onChange={(e) => setF("base_salary", e.target.value)} placeholder="0" className={inp} /></div>
                <div><label className={lbl}>Tunjangan Tetap (Rp)</label>
                  <input data-testid="emp-allowance" type="number" min="0" value={form.allowance} onChange={(e) => setF("allowance", e.target.value)} placeholder="0" className={inp} /></div>
              </div>
              {(parseInt(form.base_salary) > 0 || parseInt(form.allowance) > 0) && (
                <p className="text-[11px] text-slate-500 bg-slate-50 rounded-lg px-3 py-2 inline-block">Total {form.salary_type}: <b className="text-[#C4738A]">{formatIDR((parseInt(form.base_salary) || 0) + (parseInt(form.allowance) || 0))}</b></p>
              )}
            </section>
          )}

          {step === 3 && (
            <>
              <section className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
                <h2 className="font-display font-bold text-sm text-slate-900">Akun Login</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
                  <div><label className={lbl}>Email <span className="text-[#C4738A]">*</span></label>
                    <input data-testid="emp-email" type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} placeholder="nama@kiyana.id" className={inp} /></div>
                  <div><label className={lbl}>Password {isEdit ? <span className="text-[10px] font-normal text-slate-400">(kosongkan bila tidak diganti)</span> : <span className="text-[#C4738A]">*</span>}</label>
                    <div className="relative">
                      <input data-testid="emp-password" type={showPass ? "text" : "password"} value={form.password} onChange={(e) => setF("password", e.target.value)} placeholder={isEdit ? "••••••" : "Minimal 6 karakter"} className={`${inp} pr-10`} />
                      <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500">{showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                    </div></div>
                </div>
                <div>
                  <label className={lbl}>Permission Tambahan <span className="text-[10px] font-normal text-slate-400">(di luar bawaan role {roles.find(([v]) => v === form.role)?.[1]})</span></label>
                  <div className="border border-slate-100 rounded-xl divide-y divide-slate-50">
                    {PERM_GROUPS.map((g) => (
                      <div key={g.label} className="px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 w-28 flex-shrink-0">{g.label}</span>
                        {g.perms.map(([p, l]) => (
                          <label key={p} className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                            <input type="checkbox" data-testid={`emp-perm-${p.replace(".", "-")}`} checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} className="w-3.5 h-3.5 rounded border-slate-300 accent-[#C4738A]" />
                            {l}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
              <section className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
                <h2 className="font-display font-bold text-sm text-slate-900">Dokumen Karyawan</h2>
                <div className="flex gap-2 flex-wrap items-center">
                  <input data-testid="emp-doc-name" value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="Nama dokumen (mis. KTP, KK, Ijazah)" className={`${inp} max-w-xs`} />
                  <button data-testid="emp-doc-upload" onClick={() => docRef.current?.click()} className="h-10 px-4 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 flex items-center gap-1.5 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors">
                    <Upload className="w-3.5 h-3.5" /> Unggah Dokumen
                  </button>
                  <input ref={docRef} type="file" accept="image/*" className="hidden" onChange={pickDoc} />
                </div>
                <div className="space-y-2">
                  {form.documents.map((d, i) => (
                    <div key={i} data-testid={`emp-doc-${i}`} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2">
                      <FileKey className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <a href={d.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-slate-700 hover:text-[#C4738A] truncate">{d.name}</a>
                      <button onClick={() => setF("documents", form.documents.filter((_, idx) => idx !== i))} className="ml-auto w-7 h-7 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  {!form.documents.length && <p className="text-[11px] text-slate-400">Belum ada dokumen — unggah foto KTP/KK/Ijazah bila ada.</p>}
                </div>
              </section>
            </>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Field bertanda <span className="text-[#C4738A]">*</span> wajib diisi.</p>
            <div className="flex gap-2">
              <button data-testid="emp-save-draft" onClick={() => save(true)} disabled={!!saving} className="h-10 px-5 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 transition-colors disabled:opacity-50">
                {saving === "draft" ? "Menyimpan..." : "Simpan Draft"}
              </button>
              {step < 3 ? (
                <button data-testid="emp-next" onClick={next} className="h-10 px-6 rounded-lg text-white text-xs font-semibold transition-colors" style={{ backgroundColor: MAROON }}>Selanjutnya →</button>
              ) : (
                <button data-testid="emp-save-final" onClick={() => save(false)} disabled={!!saving} className="h-10 px-6 rounded-lg text-white text-xs font-semibold transition-colors disabled:opacity-50" style={{ backgroundColor: MAROON }}>
                  {saving === "aktif" ? "Menyimpan..." : "Simpan Karyawan"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Panel kanan */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-5 xl:sticky xl:top-4">
          <div>
            <h3 className="font-display font-bold text-sm text-slate-900 mb-3">Profil Karyawan</h3>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-slate-100 overflow-hidden border border-slate-200 flex-shrink-0">
                {form.photo ? <img src={form.photo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><User className="w-5 h-5" /></div>}
              </div>
              <div className="min-w-0">
                <div className="text-xs font-bold text-slate-800 truncate">{form.name || "Karyawan Baru"}</div>
                <div className="text-[10px] text-slate-400">{roles.find(([v]) => v === form.role)?.[1]}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] text-slate-400">ID Karyawan</div>
                <div data-testid="emp-id-display" className="font-mono text-sm font-bold text-slate-900">{empId || "KYN-····"}</div>
                <div className="text-[9px] text-slate-300">Dibuat otomatis</div>
              </div>
              <span data-testid="emp-status-badge" className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${form.active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                ● {form.active ? "Aktif" : "Draft"}
              </span>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold text-slate-700">Progres Onboarding</span>
              <span data-testid="emp-progress-label" className="text-[10px] text-slate-400">{doneCount} dari 4 bagian</span>
            </div>
            <div className="flex gap-1.5 mb-4">
              {TABS.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${stepDone[i] ? "bg-[#C4738A]" : "bg-slate-100"}`} />)}
            </div>
            <div className="space-y-3">
              {TABS.map((t, i) => (
                <button key={t.label} data-testid={`emp-step-${i}`} onClick={() => setStep(i)} className="flex items-center gap-3 w-full text-left group">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 transition-colors ${step === i ? "text-white" : stepDone[i] ? "bg-rose-100 text-[#C4738A]" : "bg-slate-100 text-slate-400"}`} style={step === i ? { backgroundColor: MAROON } : {}}>{i + 1}</span>
                  <span>
                    <span className={`block text-xs font-semibold ${step === i ? "text-slate-900" : "text-slate-600"} group-hover:text-[#C4738A] transition-colors`}>{t.label}</span>
                    <span className="block text-[10px] text-slate-400">{step === i ? "Sedang diisi" : stepDone[i] ? "Sudah diisi" : "Belum diisi"}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
